// Static demo-mode fill-in data for Invoices/index.jsx.
// Shape matches exactly what the raw `companyDatabase/{id}/invoices`
// Firestore documents look like once read via `doc.data()` in
// `fetchInvoices`, and what `invoiceService`'s formatters
// (formatDate/formatCurrency/formatBillingPeriod/getStatusBadgeColor/
// getStatusText) expect per row:
//   { invoiceNumber, type, payment: { paidAt: { seconds }, amount },
//     currency, planName, status, billingPeriod: { start: { seconds },
//     end: { seconds } }, shortUrl, createdAt: { seconds } }
//
// Dates are computed relative to "now" (days-ago) rather than hardcoded to a
// fixed 2026 calendar, so the invoice history never looks stale/lapsed no
// matter when the demo is opened — the most recent invoice always lands a
// couple weeks back with `status: 'paid'`, matching an active subscription
// whose next charge (PlanBillingTab.demoData.js's DEMO_CYCLE_END_DATE) is
// still upcoming.
const DAY_SECONDS = 24 * 60 * 60;
const nowSeconds = () => Math.floor(Date.now() / 1000);
const daysAgo = (n) => ({ seconds: nowSeconds() - n * DAY_SECONDS });

// NOTE: the real component never actually slices `invoices` by page (it
// re-fetches the *entire* collection on every page click and just bumps
// `pagination.page`), so pagination here is fixed to a single page —
// forcing "page 1 worth of data always" rather than pretending to support
// real multi-page slicing that the underlying code doesn't implement.
export const DEMO_INVOICES = [
  {
    invoiceNumber: 'INV-2026-0108',
    type: 'subscription',
    payment: { paidAt: daysAgo(12), amount: 24900 },
    currency: 'usd',
    planName: 'Growth Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(43),
      end: daysAgo(12),
    },
    shortUrl: null,
    createdAt: daysAgo(12),
  },
  {
    invoiceNumber: 'INV-2026-0107',
    type: 'subscription',
    payment: { paidAt: daysAgo(73), amount: 24900 },
    currency: 'usd',
    planName: 'Growth Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(73),
      end: daysAgo(43),
    },
    shortUrl: null,
    createdAt: daysAgo(73),
  },
  {
    invoiceNumber: 'INV-2026-0106',
    type: 'upgrade',
    payment: { paidAt: daysAgo(104), amount: 15000 },
    currency: 'usd',
    planName: 'Growth Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(104),
      end: daysAgo(73),
    },
    shortUrl: null,
    createdAt: daysAgo(104),
  },
  {
    invoiceNumber: 'INV-2026-0105',
    type: 'subscription',
    payment: { paidAt: daysAgo(134), amount: 9900 },
    currency: 'usd',
    planName: 'Starter Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(134),
      end: daysAgo(104),
    },
    shortUrl: null,
    createdAt: daysAgo(134),
  },
  {
    invoiceNumber: 'INV-2026-0104',
    type: 'subscription',
    payment: { paidAt: daysAgo(159), amount: 9900 },
    currency: 'usd',
    planName: 'Starter Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(165),
      end: daysAgo(134),
    },
    shortUrl: null,
    createdAt: daysAgo(159), // retry succeeded a few days into the period
  },
  {
    invoiceNumber: 'INV-2026-0103',
    type: 'subscription',
    payment: { paidAt: null, amount: 9900 },
    currency: 'usd',
    planName: 'Starter Plan',
    status: 'failed',
    billingPeriod: {
      start: daysAgo(165),
      end: daysAgo(134),
    },
    shortUrl: null,
    createdAt: daysAgo(161), // failed attempt, retried above
  },
  {
    invoiceNumber: 'INV-2026-0102',
    type: 'subscription',
    payment: { paidAt: daysAgo(193), amount: 9900 },
    currency: 'usd',
    planName: 'Starter Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(193),
      end: daysAgo(165),
    },
    shortUrl: null,
    createdAt: daysAgo(193),
  },
  {
    invoiceNumber: 'INV-2026-0101',
    type: 'subscription',
    payment: { paidAt: daysAgo(224), amount: 9900 },
    currency: 'usd',
    planName: 'Starter Plan',
    status: 'paid',
    billingPeriod: {
      start: daysAgo(224),
      end: daysAgo(193),
    },
    shortUrl: null,
    createdAt: daysAgo(224),
  },
];

// Single-page pagination metadata — see note above on why this doesn't
// attempt to model real multi-page slicing.
export const DEMO_PAGINATION = {
  page: 1,
  limit: DEMO_INVOICES.length,
  total: DEMO_INVOICES.length,
  totalPages: 1,
};
